/**
 * finance-core — Dự báo dòng tiền, tuổi nợ quá hạn của khoản dự kiến, gộp ứng viên mẫu định kỳ (GĐ6C).
 *
 * Quy ước:
 * - Dự báo chỉ gồm các khoản ĐÃ BIẾT (dự chi, dự thu, trả thẻ/nợ, mẫu định kỳ chưa sinh) — KHÔNG gồm chi tiêu
 *   hằng ngày; người gọi phải hiển thị mức chi biến đổi điển hình riêng để dự báo không tạo cảm giác an toàn giả.
 * - Khoản đã quá hạn mà chưa xác nhận được tính vào HÔM NAY (tiền chưa đi thật nên vẫn sắp phải đi).
 * - Chỉ tính tiền THANH KHOẢN; số dư ban đầu do người gọi truyền vào (balanceSheet().liquid).
 */

const DAY = 86_400_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const diffDays = (a: Date, b: Date) => Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / DAY);

// ───────── Tuổi quá hạn ─────────
export type AgingBucket = 'current' | 'd1_30' | 'd31_60' | 'd60plus';

/** Số ngày quá hạn (>0 = đã quá hạn). */
export const daysOverdue = (planned: Date, today: Date): number => diffDays(today, planned);

export const agingBucket = (days: number): AgingBucket =>
  days <= 0 ? 'current' : days <= 30 ? 'd1_30' : days <= 60 ? 'd31_60' : 'd60plus';

export const AGING_LABEL: Record<AgingBucket, string> = {
  current: 'Chưa đến hạn', d1_30: 'Quá hạn 1–30 ngày', d31_60: 'Quá hạn 31–60 ngày', d60plus: 'Quá hạn trên 60 ngày'
};

// ───────── Dự báo dòng tiền ─────────
export interface CashEvent {
  date: Date;
  /** Có dấu: thu dương, chi/trả âm */
  amount: number;
  label: string;
  kind: 'income' | 'expense' | 'payment';
  /** Đã quá hạn (được dồn về hôm nay) */
  overdue?: boolean;
}

export interface CashflowDay { date: Date; delta: number; balance: number; events: CashEvent[] }
export interface CashflowForecast {
  days: CashflowDay[];
  start: number;
  end: number;
  lowest: { balance: number; date: Date };
  firstNegative: Date | null;
  totalIn: number;
  totalOut: number;
  events: CashEvent[];
}

export const buildCashflow = (input: { liquidNow: number; today: Date; horizonDays: number; events: CashEvent[] }): CashflowForecast => {
  const today = startOfDay(input.today);
  const horizon = Math.max(1, Math.floor(input.horizonDays));
  const inRange: CashEvent[] = [];
  for (const e of input.events) {
    if (!Number.isFinite(e.amount) || e.amount === 0) continue;
    const off = diffDays(e.date, today);
    if (off > horizon) continue;
    inRange.push(off < 0 ? { ...e, date: today, overdue: true } : { ...e, date: startOfDay(e.date) });
  }
  inRange.sort((a, b) => a.date.getTime() - b.date.getTime());

  const days: CashflowDay[] = [];
  let bal = input.liquidNow, totalIn = 0, totalOut = 0;
  let lowest = { balance: input.liquidNow, date: today };
  let firstNegative: Date | null = bal < 0 ? today : null;
  for (let i = 0; i <= horizon; i++) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    const evs = inRange.filter(e => diffDays(e.date, date) === 0);
    const delta = evs.reduce((s, e) => s + e.amount, 0);
    for (const e of evs) e.amount > 0 ? (totalIn += e.amount) : (totalOut += -e.amount);
    bal += delta;
    days.push({ date, delta, balance: bal, events: evs });
    if (bal < lowest.balance) lowest = { balance: bal, date };
    if (bal < 0 && !firstNegative) firstNegative = date;
  }
  return { days, start: input.liquidNow, end: bal, lowest, firstNegative, totalIn, totalOut, events: inRange };
};

// ───────── Gộp ứng viên mẫu định kỳ từ dữ liệu cũ ─────────
export interface PlannedRecord {
  year?: number; month?: number; isRecurring?: boolean; amount?: number;
  category?: string; categoryName?: string; walletId?: string; walletName?: string;
  note?: string; description?: string; recurringDay?: number; plannedDate?: { toDate?: () => Date } | Date | string | null;
  paymentType?: 'credit' | 'debt'; creditCardId?: string; creditCardName?: string; debtId?: string; debtName?: string;
  sourceWalletId?: string; sourceWalletName?: string; status?: string; templateId?: string;
}

export interface TemplateCandidate {
  kind: 'expense' | 'income' | 'payment';
  name: string; amount: number; day: number;
  category?: string; walletId: string; walletName: string;
  paymentType?: 'credit' | 'debt'; creditCardId?: string; creditCardName?: string; debtId?: string; debtName?: string;
  /** Số tháng (trong dữ liệu cũ) khoản này xuất hiện */
  seenMonths: number;
}

const norm = (s?: string) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const ym = (r: PlannedRecord) => (r.year ?? 0) * 12 + (r.month ?? 0);
const dayOf = (r: PlannedRecord): number => {
  if (r.recurringDay) return Math.min(31, Math.max(1, r.recurringDay));
  const p: any = r.plannedDate; const d: Date | null = p?.toDate ? p.toDate() : p ? new Date(p) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getDate() : 1;
};

const identity = (kind: TemplateCandidate['kind'], r: PlannedRecord): string => {
  if (kind === 'payment') return `${r.paymentType}|${r.paymentType === 'debt' ? r.debtId : r.creditCardId}|${r.sourceWalletId}`;
  return `${norm(r.category || r.categoryName)}|${r.walletId}|${norm(kind === 'income' ? r.description : r.note)}`;
};

/**
 * Mỗi khoản định kỳ của THÁNG GẦN NHẤT có khoản định kỳ → một ứng viên (đúng cách bản cũ "sao chép tháng trước").
 * seenMonths = số tháng khác nhau có khoản cùng danh tính (mức tin cậy). Bỏ ứng viên đã có mẫu tương ứng.
 */
export const recurringCandidates = (
  kind: TemplateCandidate['kind'],
  items: PlannedRecord[],
  existing: Array<{ type?: string; category?: string; walletId?: string; name?: string; paymentType?: string; creditCardId?: string; debtId?: string }> = []
): TemplateCandidate[] => {
  const rec = items.filter(r => r.isRecurring && r.status !== 'cancelled' && (Number(r.amount) || 0) > 0 && r.year && r.month);
  if (rec.length === 0) return [];
  const latest = Math.max(...rec.map(ym));
  const months = new Map<string, Set<number>>();
  for (const r of rec) { const k = identity(kind, r); if (!months.has(k)) months.set(k, new Set()); months.get(k)!.add(ym(r)); }

  const covered = (c: TemplateCandidate) => existing.some(t => t.type === kind && (kind === 'payment'
    ? t.paymentType === c.paymentType && (c.paymentType === 'debt' ? t.debtId === c.debtId : t.creditCardId === c.creditCardId) && t.walletId === c.walletId
    : norm(t.category) === norm(c.category) && t.walletId === c.walletId && norm(t.name) === norm(c.name)));

  const out: TemplateCandidate[] = [];
  for (const r of rec.filter(x => ym(x) === latest)) {
    const base = { amount: Math.round(Number(r.amount)), day: dayOf(r), seenMonths: months.get(identity(kind, r))?.size ?? 1 };
    const c: TemplateCandidate = kind === 'payment'
      ? {
          kind, ...base, walletId: r.sourceWalletId ?? '', walletName: r.sourceWalletName ?? '', paymentType: r.paymentType,
          creditCardId: r.creditCardId, creditCardName: r.creditCardName, debtId: r.debtId, debtName: r.debtName,
          name: `${r.paymentType === 'debt' ? 'Trả nợ' : 'Trả thẻ'} ${r.paymentType === 'debt' ? r.debtName ?? '' : r.creditCardName ?? ''}`.trim()
        }
      : {
          kind, ...base, category: r.category || r.categoryName || 'Khác', walletId: r.walletId ?? '', walletName: r.walletName ?? '',
          name: (kind === 'income' ? r.description : r.note)?.trim() || r.category || r.categoryName || 'Khoản định kỳ'
        };
    if (!c.walletId || covered(c)) continue;
    out.push(c);
  }
  return out.sort((a, b) => a.day - b.day);
};

export interface TemplateLike {
  id: string; type?: string; name?: string; category?: string; walletId?: string; paymentType?: string; creditCardId?: string; debtId?: string;
  frequency?: string; dayOfMonth?: number; monthOfYear?: number; createdAt?: { toDate?: () => Date } | null;
}

const tplIdentity = (t: TemplateLike): string => t.type === 'payment'
  ? `payment|${t.paymentType}|${t.paymentType === 'debt' ? t.debtId : t.creditCardId}|${t.walletId}|${t.dayOfMonth}`
  : `${t.type}|${norm(t.category)}|${t.walletId}|${norm(t.name)}|${t.frequency}|${t.dayOfMonth}|${t.monthOfYear ?? ''}`;

/**
 * Mẫu định kỳ TRÙNG (cùng loại, danh mục/thẻ, ví, tên, ngày): giữ bản tạo sớm nhất, trả về id các bản thừa.
 * Mẫu trùng sẽ sinh hai khoản mỗi tháng nên cần gỡ.
 */
export const duplicateTemplateIds = (templates: TemplateLike[]): string[] => {
  const time = (t: TemplateLike) => t.createdAt?.toDate?.().getTime() ?? 0;
  const groups = new Map<string, TemplateLike[]>();
  for (const t of templates) { const k = tplIdentity(t); groups.set(k, [...(groups.get(k) ?? []), t]); }
  const extra: string[] = [];
  for (const g of groups.values()) {
    if (g.length < 2) continue;
    const keep = [...g].sort((a, b) => time(a) - time(b) || a.id.localeCompare(b.id))[0];
    for (const t of g) if (t.id !== keep.id) extra.push(t.id);
  }
  return extra;
};

// ───────── Phân tích thiếu hụt thanh khoản ─────────
export interface ShortfallPlan {
  /** Số tiền thiếu ở điểm thấp nhất của dự báo (luôn > 0) */
  gap: number;
  firstNegative: Date;
  lowestDate: Date;
  /** Trả thẻ & nợ phải ra trước điểm thấp nhất (khó dời) */
  fixedOut: number;
  /** Chi tiêu dự kiến trước điểm thấp nhất (có thể dời hoặc giảm) */
  flexibleOut: number;
  /** Thu dự kiến đã có trước điểm thấp nhất */
  incomeIn: number;
  /** Các khoản chi tiêu lớn nhất có thể xem xét dời/giảm */
  flexible: CashEvent[];
  /** Bỏ HẾT chi tiêu dự kiến thì còn thiếu bao nhiêu (≤ 0 nghĩa là đủ) */
  gapAfterFlexible: number;
}

/**
 * Khi dự báo cho thấy thanh khoản âm: tách phần thiếu thành khoản khó dời (trả thẻ & nợ) và khoản có thể điều chỉnh
 * (chi tiêu dự kiến). Chỉ dựa trên khoản ĐÃ BIẾT — không tính chi tiêu hằng ngày chưa lên kế hoạch.
 */
export const shortfallPlan = (f: CashflowForecast, topN = 5): ShortfallPlan | null => {
  if (!f.firstNegative || f.lowest.balance >= 0) return null;
  const upTo = f.lowest.date.getTime();
  const evs = f.events.filter(e => e.date.getTime() <= upTo);
  const sumOf = (pred: (e: CashEvent) => boolean) => evs.filter(pred).reduce((s, e) => s + Math.abs(e.amount), 0);
  const fixedOut = sumOf(e => e.kind === 'payment' && e.amount < 0);
  const flexibleOut = sumOf(e => e.kind === 'expense' && e.amount < 0);
  const incomeIn = sumOf(e => e.amount > 0);
  const gap = -f.lowest.balance;
  return {
    gap, firstNegative: f.firstNegative, lowestDate: f.lowest.date, fixedOut, flexibleOut, incomeIn,
    flexible: evs.filter(e => e.kind === 'expense' && e.amount < 0).sort((a, b) => a.amount - b.amount).slice(0, topN),
    gapAfterFlexible: gap - flexibleOut
  };
};
